/**
 * A IMAGEM GERADA NÃO CABE NO LUGAR ONDE ELA É GUARDADA.
 *
 * ── O DEFEITO, MEDIDO (`T5.6`) ────────────────────────────────────────────────────────────────
 *
 * Ele, na letra: *"E eu não consegui salvar o quadrinho. Não deu pra salvar o quadrinho."* — e a
 * segunda frase **é a mensagem de erro deste produto**, palavra por palavra. O salvar falhou e
 * avisou; o que ninguém sabia era por quê.
 *
 * Um desenho gerado vira um documento em `users/{uid}/imgs`, com a imagem inteira em base64 dentro
 * do campo `url`. **Um documento do Firestore cabe em 1 MiB.** Medido em 15/09, contra produção:
 *
 * ```
 * "um circulo azul"                              685 KB    cabe
 * "um gato gordo"                               1,12 MiB   NÃO CABE
 * "uma cidade cheia de predios e pessoas"       1,60 MiB   NÃO CABE
 * ```
 *
 * Quanto mais detalhe o desenho tem, maior o PNG — e é por isso que o defeito parece aleatório: um
 * teste bobo salva, e a história de verdade não. A do caderno que ele ditou tinha cidade, pessoas e
 * objetos.
 *
 * E os DOIS caminhos de salvar sofrem do mesmo: o `salvarNoAcervo` do roteirizador e o `saveImg`
 * que a tela de gerar imagem usa para pôr o desenho na prateleira.
 *
 * ── O QUE ISTO FAZ, E O QUE NÃO FAZ ──────────────────────────────────────────────────────────
 *
 * Reencoda a imagem para caber, tentando na ordem: primeiro só baixar a qualidade, e só depois
 * reduzir o tamanho — porque perder nitidez dói menos que perder pixels, e uma folha A4 impressa
 * quer os pixels.
 *
 * **WebP e não JPEG**, e isso não é preferência: o recorte de fundo (`T4.8`) produz imagem com
 * transparência, e JPEG não tem canal alfa — salvar um personagem recortado como JPEG devolveria
 * um retângulo com fundo preto. WebP guarda o alfa.
 *
 * Medido no navegador, sobre 1024×1024 de **ruído colorido puro** — o pior caso que existe para
 * qualquer codec, e muito pior que arte:
 *
 * ```
 * PNG original                     4679 KB
 * WebP q=0,90                      1143 KB
 * WebP q=0,80                       937 KB
 * WebP q=0,80 a 768px               441 KB
 * ```
 *
 * Se nem o ruído estoura o orçamento na terceira tentativa, desenho não estoura.
 *
 * **O que isto NÃO é: o conserto definitivo.** Imagem não deveria morar dentro de documento de
 * banco — deveria estar no Storage, com URL. Isso é infraestrutura que ninguém pediu em ponto
 * nenhum, e trocar o lugar de guardar mexe nas regras, na leitura do acervo e no que já está
 * gravado. O que dá para fazer sem inventar escopo é **caber**, e dizer aqui por quê.
 */

/** O teto por imagem. 900 KiB deixa ~148 KiB de folga sob o 1 MiB do documento, para o resto dele. */
export const TETO_DA_IMAGEM = 900 * 1024;

export interface Tentativa {
  /** Qualidade do WebP, de 0 a 1. */
  qualidade: number;
  /** Fração do tamanho original. 1 = não reduz. */
  escala: number;
}

/**
 * A ESCADA DE TENTATIVAS, e a ordem dela é a decisão.
 *
 * Qualidade primeiro, tamanho depois: as três primeiras tentativas mantêm os pixels e só baixam a
 * nitidez; as três últimas encolhem. Numa folha A4 impressa, pixel perdido não volta — nitidez
 * perdida ninguém nota num quadro de 8 cm.
 *
 * Pura, e por isso testável. A parte que precisa de `<canvas>` é a de baixo, e é fina.
 */
export function escadaDeTentativas(): Tentativa[] {
  return [
    { qualidade: 0.92, escala: 1 },
    { qualidade: 0.85, escala: 1 },
    { qualidade: 0.78, escala: 1 },
    { qualidade: 0.85, escala: 0.8 },
    { qualidade: 0.78, escala: 0.65 },
    { qualidade: 0.7, escala: 0.5 },
  ];
}

/** Quanto pesa, em bytes, a string que vai para o Firestore. */
export function pesoDaImagem(dataUri: string): number {
  return dataUri.length;
}

/** Já cabe? Então não se toca nela — encolher o que cabe é perder qualidade de graça. */
export function jaCabe(dataUri: string, teto: number = TETO_DA_IMAGEM): boolean {
  return pesoDaImagem(dataUri) <= teto;
}

/** O que aconteceu com uma imagem, para quem chama poder contar. */
export interface ResultadoDoEncolhimento {
  imagem: string;
  /** `null` quando não precisou mexer. */
  tentativaUsada: Tentativa | null;
  bytesAntes: number;
  bytesDepois: number;
  /** Nenhuma tentativa coube. A imagem volta na MENOR que se conseguiu. */
  aindaNaoCabe: boolean;
}

/**
 * Encolhe até caber no teto.
 *
 * Devolve a imagem original quando ela já cabe. Se nenhuma tentativa couber — o que a medição diz
 * ser improvável —, devolve a menor que conseguiu, com `aindaNaoCabe: true`: é melhor tentar
 * gravar o menor possível e falhar com o erro do Firestore do que decidir aqui que a pessoa perde
 * o desenho.
 */
export async function encolherParaCaber(
  dataUri: string,
  teto: number = TETO_DA_IMAGEM,
  desenhar: typeof reencodar = reencodar
): Promise<ResultadoDoEncolhimento> {
  const bytesAntes = pesoDaImagem(dataUri);
  if (bytesAntes <= teto) {
    return { imagem: dataUri, tentativaUsada: null, bytesAntes, bytesDepois: bytesAntes, aindaNaoCabe: false };
  }

  let menor = dataUri;
  let menorTentativa: Tentativa | null = null;

  for (const tentativa of escadaDeTentativas()) {
    let candidata: string;
    try {
      candidata = await desenhar(dataUri, tentativa);
    } catch {
      // Um formato que o navegador não produz, ou uma imagem que não decodifica: pula a
      // tentativa em vez de derrubar o salvar inteiro.
      continue;
    }
    if (pesoDaImagem(candidata) < pesoDaImagem(menor)) {
      menor = candidata;
      menorTentativa = tentativa;
    }
    if (pesoDaImagem(candidata) <= teto) {
      return {
        imagem: candidata,
        tentativaUsada: tentativa,
        bytesAntes,
        bytesDepois: pesoDaImagem(candidata),
        aindaNaoCabe: false,
      };
    }
  }

  return {
    imagem: menor,
    tentativaUsada: menorTentativa,
    bytesAntes,
    bytesDepois: pesoDaImagem(menor),
    aindaNaoCabe: true,
  };
}

/**
 * A parte que precisa do navegador: desenha num `<canvas>` e reencoda.
 *
 * Fica separada e injetável para que `encolherParaCaber` seja testável sem `<canvas>` — o `jsdom`
 * dos testes não implementa `getContext("2d")`, e uma função que precisa dele só se confere à mão.
 */
export async function reencodar(dataUri: string, tentativa: Tentativa): Promise<string> {
  const img = new Image();
  img.src = dataUri;
  await img.decode();

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.naturalWidth * tentativa.escala));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * tentativa.escala));

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("sem contexto 2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  const saida = canvas.toDataURL("image/webp", tentativa.qualidade);
  // Navegador sem WebP devolve PNG silenciosamente, e PNG reencodado costuma ficar MAIOR que o
  // original. Dizer que falhou deixa a escada seguir para a tentativa que reduz tamanho.
  if (!saida.startsWith("data:image/webp")) throw new Error("webp não suportado");
  return saida;
}
